import React, { useEffect, useState, useRef, useCallback } from 'react';
import AdminLayout from './AdminLayout';
import { useAuth } from '../../context/AuthContext';
import ConfirmModal from '../../components/ConfirmModal';
import api from '../../api/axios';
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  X,
  Trash2,
  Calendar,
  Pencil,
  Clock
} from 'lucide-react';

const EVENT_TYPES = [
  { key: 'egitim', label: 'Eğitim', color: '#007aff', bg: 'rgba(0,122,255,0.10)' },
  { key: 'faaliyet', label: 'Faaliyet', color: '#34c759', bg: 'rgba(52,199,89,0.10)' },
  { key: 'duyuru', label: 'Duyuru', color: '#ff9500', bg: 'rgba(255,149,0,0.10)' },
  { key: 'toplanti', label: 'Toplantı', color: '#af52de', bg: 'rgba(175,82,222,0.10)' },
  { key: 'diger', label: 'Diğer', color: '#6b7280', bg: 'rgba(107,114,128,0.10)' }
];

const MONTHS_TR = [
  'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'
];

const DAYS_TR = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobile;
};

const EMPTY_FORM = {
  title: '',
  description: '',
  event_time: '09:00',
  event_type: 'egitim',
  color: '#007aff'
};

export default function AdminCalendar() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isMobile = useIsMobile();

  const today = new Date();

  const [currentDate, setCurrentDate] = useState({
    month: today.getMonth(),
    year: today.getFullYear()
  });

  const [events, setEvents] = useState([]);
  const [calendarView, setCalendarView] = useState('month');

  // Modal durumları birbirinden tamamen ayrı.
  const [viewingEvent, setViewingEvent] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [showFormModal, setShowFormModal] = useState(false);

  const [selectedDay, setSelectedDay] = useState(null);
  const [longPressDay, setLongPressDay] = useState(null);
  const longPressTimer = useRef(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const startLongPress = day => {
    if (!isMobile) return;

    longPressTimer.current = setTimeout(() => {
      setLongPressDay(day);
      longPressTimer.current = null;
    }, 600);
  };

  const cancelLongPress = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const handleDayClick = day => {
    if (longPressDay !== null) {
      setLongPressDay(null);
      return;
    }

    openAddModal(day);
  };

  const closeLongPressModal = () => {
    setLongPressDay(null);
  };

  const [confirmModal, setConfirmModal] = useState({
    open: false,
    action: null,
    title: '',
    message: ''
  });

  const showConfirm = (title, message, action) => {
    setConfirmModal({
      open: true,
      title,
      message,
      action
    });
  };

  const closeConfirm = () => {
    setConfirmModal({
      open: false,
      action: null,
      title: '',
      message: ''
    });
  };

  const loadEvents = useCallback(() => {
    api
      .get(`/calendar?month=${currentDate.month + 1}&year=${currentDate.year}`)
      .then(response => {
        setEvents(Array.isArray(response.data) ? response.data : []);
      })
      .catch(error => {
        console.error('Takvim yüklenemedi:', error);
      });
  }, [currentDate]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month, year) => {
    const day = new Date(year, month, 1).getDay();
    return day === 0 ? 6 : day - 1;
  };

  const getEventsForDay = day => {
    const dateStr =
      `${currentDate.year}-` +
      `${String(currentDate.month + 1).padStart(2, '0')}-` +
      `${String(day).padStart(2, '0')}`;

    return events.filter(event => {
      const eventDate = event.event_date
        ? String(event.event_date).substring(0, 10)
        : '';

      return eventDate === dateStr;
    });
  };

  const getTypeInfo = key => {
    return EVENT_TYPES.find(type => type.key === key) || EVENT_TYPES[0];
  };

  const prevMonth = () => {
    setCurrentDate(date =>
      date.month === 0
        ? { month: 11, year: date.year - 1 }
        : { month: date.month - 1, year: date.year }
    );
  };

  const nextMonth = () => {
    setCurrentDate(date =>
      date.month === 11
        ? { month: 0, year: date.year + 1 }
        : { month: date.month + 1, year: date.year }
    );
  };

  const goToday = () => {
    const now = new Date();

    setCurrentDate({
      month: now.getMonth(),
      year: now.getFullYear()
    });
  };

  /*
   * YENİ ETKİNLİK
   */
  const openAddModal = day => {
    setViewingEvent(null);
    setEditingEvent(null);

    setSelectedDay(day);

    setForm({
      ...EMPTY_FORM
    });

    setShowFormModal(true);
  };

  /*
   * GÖRÜNTÜLEME
   * Etkinliğe tıklanınca artık doğrudan düzenleme açılmaz.
   */
  const openViewModal = (event, e) => {
    if (e) {
      e.stopPropagation();
    }

    setShowFormModal(false);
    setEditingEvent(null);
    setViewingEvent(event);
  };

  /*
   * DÜZENLEME
   * Sadece görüntüleme ekranındaki "Düzenle" butonundan çağrılır.
   */
  const openEditModal = event => {
    if (!event) return;

    setViewingEvent(null);
    setEditingEvent(event);

    setSelectedDay(
      parseInt(String(event.event_date || '').substring(8, 10), 10)
    );

    setForm({
      title: event.title || '',
      description: event.description || '',
      event_time: event.event_time || '09:00',
      event_type: event.event_type || 'egitim',
      color: event.color || getTypeInfo(event.event_type).color
    });

    setShowFormModal(true);
  };

  const closeFormModal = () => {
    if (saving) return;

    setShowFormModal(false);
    setEditingEvent(null);
    setSelectedDay(null);
    setForm({
      ...EMPTY_FORM
    });
  };

  const closeViewModal = () => {
    setViewingEvent(null);
  };

  /*
   * KAYDET / GÜNCELLE
   */
  const save = async () => {
    if (!form.title.trim() || saving) return;

    setSaving(true);

    try {
      let dayNum = selectedDay;

      if (!dayNum && editingEvent) {
        dayNum = parseInt(
          String(editingEvent.event_date || '').substring(8, 10),
          10
        );
      }

      if (!dayNum) {
        throw new Error('Etkinlik tarihi bulunamadı.');
      }

      const dateStr =
        `${currentDate.year}-` +
        `${String(currentDate.month + 1).padStart(2, '0')}-` +
        `${String(dayNum).padStart(2, '0')}`;

      const payload = {
        ...form,
        title: form.title.trim(),
        event_date: dateStr
      };

      if (editingEvent) {
        await api.put(`/calendar/${editingEvent.id}`, payload);
      } else {
        await api.post('/calendar', payload);
      }

      closeFormModal();
      loadEvents();
    } catch (error) {
      console.error('Takvim kayıt hatası:', error);
      alert('Etkinlik kaydedilirken bir hata oluştu.');
    } finally {
      setSaving(false);
    }
  };

  /*
   * SİL
   */
  const deleteEvent = (id, e) => {
    if (e) {
      e.stopPropagation();
    }

    showConfirm(
      'Etkinliği Sil',
      'Bu etkinliği silmek istediğinize emin misiniz?',
      async () => {
        try {
          await api.delete(`/calendar/${id}`);

          setViewingEvent(null);
          setShowFormModal(false);
          setEditingEvent(null);

          loadEvents();
        } catch (error) {
          console.error('Etkinlik silme hatası:', error);
          alert('Etkinlik silinirken bir hata oluştu.');
        } finally {
          closeConfirm();
        }
      }
    );
  };

  const daysInMonth = getDaysInMonth(
    currentDate.month,
    currentDate.year
  );

  const firstDay = getFirstDayOfMonth(
    currentDate.month,
    currentDate.year
  );

  // HAFTA GÖRÜNÜMÜ
  const getWeekStart = () => {
    const date = new Date(
      currentDate.year,
      currentDate.month,
      today.getDate()
    );

    const day = date.getDay();
    const diff = day === 0 ? -6 : 1 - day;

    date.setDate(date.getDate() + diff);
    return date;
  };

  const weekStart = getWeekStart();

  const weekDays = Array.from({ length: 7 }).map((_, index) => {
    const date = new Date(weekStart);
    date.setDate(weekStart.getDate() + index);
    return date;
  });

  const getEventsForDate = date => {
    const dateStr =
      `${date.getFullYear()}-` +
      `${String(date.getMonth() + 1).padStart(2, '0')}-` +
      `${String(date.getDate()).padStart(2, '0')}`;

    return events.filter(event => {
      const eventDate = event.event_date
        ? String(event.event_date).substring(0, 10)
        : '';

      return eventDate === dateStr;
    });
  };

  const isToday = day => {
    return (
      day === today.getDate() &&
      currentDate.month === today.getMonth() &&
      currentDate.year === today.getFullYear()
    );
  };

  const formatEventDate = event => {
    if (!event?.event_date) return '';

    const dateText = String(event.event_date).substring(0, 10);
    const [year, month, day] = dateText.split('-').map(Number);

    if (!year || !month || !day) return '';

    return new Date(year, month - 1, day).toLocaleDateString(
      'tr-TR',
      {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      }
    );
  };

  return (
    <AdminLayout>

      {/* SAYFA BAŞLIĞI */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 20,
          gap: 12,
          flexWrap: 'wrap'
        }}
      >
        <h2
          style={{
            margin: 0,
            fontWeight: 700,
            fontSize: isMobile ? 19 : 22,
            color: '#1a1a2e',
            display: 'flex',
            alignItems: 'center',
            gap: 10
          }}
        >
          <Calendar size={22} color="#007aff" />
          Eğitim Takvimi
        </h2>

        <button
          onClick={goToday}
          style={{
            border: '1px solid #e5e7eb',
            background: 'white',
            color: '#374151',
            padding: '8px 14px',
            borderRadius: 10,
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: 12
          }}
        >
          Bugün
        </button>
      </div>

      {/* TAKVİM */}
      <div
        style={{
          background: 'white',
          borderRadius: 20,
          overflow: 'hidden',
          boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
          border: '1px solid #eef0f4'
        }}
      >

        {/* TAKVİM ÜST BAR */}
        <div
          style={{
            background: 'linear-gradient(135deg, #1a1a2e, #007aff)',
            padding: isMobile ? '14px 16px' : '18px 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <button
            onClick={prevMonth}
            style={{
              background: 'rgba(255,255,255,0.15)',
              border: 'none',
              borderRadius: 10,
              width: 36,
              height: 36,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white'
            }}
          >
            <ChevronLeft size={18} />
          </button>

          <div
            style={{
              fontWeight: 800,
              fontSize: isMobile ? 16 : 20,
              color: 'white'
            }}
          >
            {MONTHS_TR[currentDate.month]} {currentDate.year}
          </div>

          <button
            onClick={nextMonth}
            style={{
              background: 'rgba(255,255,255,0.15)',
              border: 'none',
              borderRadius: 10,
              width: 36,
              height: 36,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white'
            }}
          >
            <ChevronRight size={18} />
          </button>
        </div>

                  {/* GÖRÜNÜM SEÇİMİ */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              padding: '8px 12px',
              background: '#f8fafc',
              borderBottom: '1px solid #eef0f4'
            }}
          >
            <div
              style={{
                display: 'flex',
                background: '#e5e7eb',
                borderRadius: 10,
                padding: 3
              }}
            >
              <button
                onClick={() => setCalendarView('month')}
                style={{
                  border: 'none',
                  borderRadius: 8,
                  padding: '7px 18px',
                  cursor: 'pointer',
                  fontWeight: 700,
                  fontSize: 12,
                  background: calendarView === 'month' ? 'white' : 'transparent',
                  color: calendarView === 'month' ? '#007aff' : '#64748b',
                  boxShadow: calendarView === 'month'
                    ? '0 1px 4px rgba(0,0,0,0.12)'
                    : 'none'
                }}
              >
                Ay
              </button>

              <button
                onClick={() => setCalendarView('week')}
                style={{
                  border: 'none',
                  borderRadius: 8,
                  padding: '7px 18px',
                  cursor: 'pointer',
                  fontWeight: 700,
                  fontSize: 12,
                  background: calendarView === 'week' ? 'white' : 'transparent',
                  color: calendarView === 'week' ? '#007aff' : '#64748b',
                  boxShadow: calendarView === 'week'
                    ? '0 1px 4px rgba(0,0,0,0.12)'
                    : 'none'
                }}
              >
                Hafta
              </button>
            </div>
          </div>


          {/* TAKVİM GÖRÜNÜMÜ */}

          {calendarView === 'week' ? (
            <>
              {/* HAFTA BAŞLIKLARI */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(7, 1fr)',
                  background: '#f8fafc',
                  borderBottom: '1px solid #eef0f4'
                }}
              >
                {weekDays.map((date, index) => {
                  const isCurrentToday =
                    date.toDateString() === today.toDateString();

                  return (
                    <div
                      key={date.toISOString()}
                      style={{
                        padding: isMobile ? '9px 2px' : '12px 6px',
                        textAlign: 'center',
                        borderRight:
                          index < 6
                            ? '1px solid #eef0f4'
                            : 'none'
                      }}
                    >
                      <div
                        style={{
                          fontSize: isMobile ? 9 : 11,
                          fontWeight: 700,
                          color:
                            index >= 5
                              ? '#ef4444'
                              : '#64748b',
                          marginBottom: 5
                        }}
                      >
                        {DAYS_TR[index]}
                      </div>

                      <div
                        style={{
                          width: isMobile ? 27 : 34,
                          height: isMobile ? 27 : 34,
                          margin: '0 auto',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: isCurrentToday
                            ? '#007aff'
                            : 'transparent',
                          color: isCurrentToday
                            ? 'white'
                            : index >= 5
                              ? '#ef4444'
                              : '#334155',
                          fontWeight: 800,
                          fontSize: isMobile ? 11 : 14
                        }}
                      >
                        {date.getDate()}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* HAFTA ETKİNLİKLERİ */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(7, 1fr)',
                  minHeight: isMobile ? 300 : 420
                }}
              >
                {weekDays.map((date, index) => {
                  const dayEvents = getEventsForDate(date);

                  const isCurrentToday =
                    date.toDateString() === today.toDateString();

                  return (
                    <div
                      key={date.toISOString()}
                      onClick={() => handleDayClick(date.getDate())}
                      onContextMenu={e => e.preventDefault()}
                      onSelectStart={e => e.preventDefault()}
                      style={{
                        minWidth: 0,
                        padding: isMobile ? 5 : 9,
                        borderRight:
                          index < 6
                            ? '1px solid #eef0f4'
                            : 'none',
                        background: isCurrentToday
                          ? 'rgba(0,122,255,0.035)'
                          : 'white',
                        userSelect: 'none',
                        WebkitUserSelect: 'none',
                        WebkitTouchCallout: 'none',
                        WebkitTapHighlightColor: 'transparent',
                        cursor: 'pointer'
                      }}
                    >
                      {dayEvents.length === 0 ? (
                        <div
                          style={{
                            color: '#cbd5e1',
                            textAlign: 'center',
                            paddingTop: 25,
                            fontSize: 11
                          }}
                        >
                          Etkinlik yok
                        </div>
                      ) : (
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 7
                          }}
                        >
                          {dayEvents.map(event => {
                            const typeInfo =
                              getTypeInfo(event.event_type);

                            return (
                              <div
                                key={event.id}
                                onClick={e => {
                                  e.stopPropagation();
                                  openViewModal(event, e);
                                }}
                                onContextMenu={e =>
                                  e.preventDefault()
                                }
                                style={{
                                  padding: isMobile
                                    ? '7px 5px'
                                    : '9px 8px',
                                  borderRadius: 9,
                                  borderLeft:
                                    `4px solid ${typeInfo.color}`,
                                  background: typeInfo.bg,
                                  color: typeInfo.color,
                                  fontSize: isMobile ? 9 : 12,
                                  fontWeight: 700,
                                  lineHeight: 1.3,
                                  wordBreak: 'break-word',
                                  userSelect: 'none',
                                  WebkitUserSelect: 'none',
                                  WebkitTouchCallout: 'none',
                                  cursor: 'pointer'
                                }}
                              >
                                {event.title}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
          <>
          {/* GÜN BAŞLIKLARI */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            background: '#f8fafc',
            borderBottom: '1px solid #eef0f4'
          }}
        >
          {DAYS_TR.map(day => (
            <div
              key={day}
              style={{
                padding: isMobile ? '8px 2px' : '11px 8px',
                textAlign: 'center',
                fontWeight: 700,
                fontSize: isMobile ? 9 : 11,
                color:
                  day === 'Cmt' || day === 'Paz'
                    ? '#ef4444'
                    : '#64748b',
                textTransform: 'uppercase'
              }}
            >
              {day}
            </div>
          ))}
        </div>

        {/* GRID */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)'
          }}
        >

          {/* BOŞ GÜNLER */}
          {Array.from({ length: firstDay }).map((_, index) => (
            <div
              key={`empty-${index}`}
              style={{
                height: isMobile ? 82 : 125,
                borderRight: '1px solid #eef0f4',
                borderBottom: '1px solid #eef0f4',
                background: '#fafafa'
              }}
            />
          ))}

          {/* GÜNLER */}
          {Array.from({ length: daysInMonth }).map((_, index) => {
            const day = index + 1;
            const dayEvents = getEventsForDay(day);
            const weekend = (firstDay + index) % 7 >= 5;
            const todayDay = isToday(day);

            return (
              <div
                key={day}
                onClick={() => handleDayClick(day)}
                  onContextMenu={e => e.preventDefault()}
                  onSelectStart={e => e.preventDefault()}
                  onTouchStart={() => startLongPress(day)}
                  onTouchEnd={cancelLongPress}
                  onTouchMove={cancelLongPress}
                  onMouseDown={() => startLongPress(day)}
                  onMouseUp={cancelLongPress}
                  onMouseLeave={cancelLongPress}
                style={{
                  height: isMobile ? 82 : 125,
                  minWidth: 0,
                  userSelect: 'none',
                  WebkitUserSelect: 'none',
                  WebkitTouchCallout: 'none',
                  WebkitTapHighlightColor: 'transparent',
                  overflow: 'hidden',
                  boxSizing: 'border-box',
                  padding: isMobile ? 5 : 8,
                  borderRight: '1px solid #eef0f4',
                  borderBottom: '1px solid #eef0f4',
                  cursor: 'pointer',
                  background: todayDay
                    ? 'rgba(0,122,255,0.045)'
                    : weekend
                      ? 'rgba(239,68,68,0.018)'
                      : 'white',
                  position: 'relative'
                }}
              >

                {/* GÜN NUMARASI */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 5
                  }}
                >
                  <div
                    style={{
                      width: isMobile ? 22 : 27,
                      height: isMobile ? 22 : 27,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: todayDay
                        ? '#007aff'
                        : 'transparent',
                      color: todayDay
                        ? 'white'
                        : weekend
                          ? '#ef4444'
                          : '#334155',
                      fontWeight: todayDay ? 800 : 600,
                      fontSize: isMobile ? 10 : 13
                    }}
                  >
                    {day}
                  </div>

                  {dayEvents.length === 0 && (
                    <Plus
                      size={13}
                      color="#cbd5e1"
                    />
                  )}
                </div>

                {/* ETKİNLİKLER */}
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3,
                    overflow: 'hidden',
                    maxHeight: isMobile ? 49 : 84
                  }}
                >
                  {dayEvents.slice(0, 3).map(event => {
                    const typeInfo = getTypeInfo(event.event_type);

                    return (
                      <div
                        key={event.id}
                        onClick={e => openViewModal(event, e)}
                          onContextMenu={e => e.preventDefault()}
                        title={event.title}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          minWidth: 0,
                          userSelect: 'none',
                          WebkitUserSelect: 'none',
                          WebkitTouchCallout: 'none',
                          WebkitTapHighlightColor: 'transparent',
                          height: isMobile ? 15 : 23,
                          padding: isMobile
                            ? '1px 3px'
                            : '2px 7px',
                          borderRadius: '0 5px 5px 0',
                          borderLeft:
                            `3px solid ${typeInfo.color}`,
                          background: typeInfo.bg,
                          color: typeInfo.color,
                          fontSize: isMobile ? 8 : 11,
                          fontWeight: 650,
                          cursor: 'pointer',
                          boxSizing: 'border-box',
                          overflow: 'hidden'
                        }}
                      >
                        <span
                          style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            minWidth: 0,
                            flex: 1
                          }}
                        >
                          {event.title}
                        </span>

                        {isAdmin && (
                          <button
                            onClick={e => deleteEvent(event.id, e)}
                            title="Sil"
                            style={{
                              border: 'none',
                              background: 'transparent',
                              padding: 0,
                              marginLeft: 4,
                              cursor: 'pointer',
                              color: typeInfo.color,
                              display: 'flex',
                              flexShrink: 0
                            }}
                          >
                            <X size={10} />
                          </button>
                        )}
                      </div>
                    );
                  })}

                  {dayEvents.length > 3 && (
                    <div
                      style={{
                        fontSize: isMobile ? 8 : 10,
                        color: '#94a3b8',
                        fontWeight: 600,
                        paddingLeft: 5
                      }}
                    >
                      +{dayEvents.length - 3} daha
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* LEJANT */}
        <div
          style={{
            padding: isMobile ? '12px' : '15px 20px',
            borderTop: '1px solid #eef0f4',
            display: 'flex',
            gap: isMobile ? 10 : 18,
            flexWrap: 'wrap'
          }}
        >
          {EVENT_TYPES.map(type => (
            <div
              key={type.key}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6
              }}
            >
              <div
                style={{
                  width: 9,
                  height: 9,
                  borderRadius: 3,
                  background: type.color
                }}
              />

              <span
                style={{
                  fontSize: 11,
                  color: '#64748b',
                  fontWeight: 500
                }}
              >
                {type.label}
              </span>
            </div>
          ))}
        </div>
              </>
            )}
        </div>


        {/* =====================================================
            GÖRÜNTÜLEME MODALI
           ===================================================== */}
        
        {/* LONG PRESS DAY MODAL */}
      {longPressDay !== null && (
        <div
          onClick={closeLongPressModal}
          style={{
              position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: isMobile ? 15 : 20
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
                userSelect: 'none',
                WebkitUserSelect: 'none',
                WebkitTouchCallout: 'none',
                touchAction: 'manipulation',

              width: '100%',
              maxWidth: 620,
              maxHeight: isMobile ? '82vh' : '75vh',
              overflowY: 'auto',
              background: 'white',
              borderRadius: 24,
              boxShadow: '0 20px 70px rgba(0,0,0,0.25)'
            }}
          >
            {/* HEADER */}
            <div
              style={{
                padding: '20px',
                borderBottom: '1px solid #eef0f4',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                position: 'sticky',
                top: 0,
                background: 'white',
                zIndex: 2
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: 12,
                    color: '#64748b',
                    fontWeight: 600,
                    marginBottom: 4
                  }}
                >
                  GÜNÜN ETKİNLİKLERİ
                </div>

                <div
                  style={{
                    fontSize: 21,
                    fontWeight: 800,
                    color: '#0f172a'
                  }}
                >
                  {longPressDay}. {MONTHS_TR[currentDate.month]} {currentDate.year}
                </div>
              </div>

              <button
                onClick={closeLongPressModal}
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: '50%',
                  border: 'none',
                  background: '#f1f5f9',
                  color: '#475569',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer'
                }}
              >
                <X size={18} />
              </button>
            </div>

            {/* EVENTS */}
            <div style={{ padding: 20 }}>
              {(() => {
                const selectedEvents = getEventsForDay(longPressDay);

                if (selectedEvents.length === 0) {
                  return (
                    <div
                      style={{
                        textAlign: 'center',
                        padding: '35px 15px',
                        color: '#94a3b8'
                      }}
                    >
                      <Calendar
                        size={34}
                        style={{ marginBottom: 10, opacity: 0.6 }}
                      />

                      <div
                        style={{
                          fontSize: 15,
                          fontWeight: 700,
                          color: '#64748b'
                        }}
                      >
                        Bu günde etkinlik yok
                      </div>

                      <div
                        style={{
                          fontSize: 12,
                          marginTop: 5
                        }}
                      >
                        Yeni etkinlik eklemek için kapatıp güne dokunun.
                      </div>
                    </div>
                  );
                }

                return (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 10
                    }}
                  >
                    {selectedEvents.map(event => {
                      const typeInfo = getTypeInfo(event.event_type);

                      return (
                        <div
                          key={event.id}
                          onClick={e => {
                            closeLongPressModal();
                            openViewModal(event, e);
                          }}
                          style={{
                            padding: '14px 15px',
                            borderRadius: 14,
                            borderLeft: `5px solid ${typeInfo.color}`,
                            background: typeInfo.bg,
                            cursor: 'pointer'
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              marginBottom: 6
                            }}
                          >
                            <span
                              style={{
                                fontSize: 11,
                                fontWeight: 800,
                                color: typeInfo.color,
                                textTransform: 'uppercase'
                              }}
                            >
                              {typeInfo.label}
                            </span>

                            {event.event_time && (
                              <span
                                style={{
                                  fontSize: 11,
                                  fontWeight: 700,
                                  color: '#64748b'
                                }}
                              >
                                • {String(event.event_time).substring(0, 5)}
                              </span>
                            )}
                          </div>

                          <div
                            style={{
                              fontSize: 15,
                              fontWeight: 800,
                              color: '#0f172a',
                              lineHeight: 1.35,
                              wordBreak: 'break-word'
                            }}
                          >
                            {event.title}
                          </div>

                          {event.description && (
                            <div
                              style={{
                                marginTop: 6,
                                fontSize: 12,
                                color: '#64748b',
                                lineHeight: 1.4
                              }}
                            >
                              {event.description}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {viewingEvent && (
        <div
          onClick={closeViewModal}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15,23,42,0.48)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1100,
            padding: 20
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              width: '100%',
              maxWidth: 520,
              background: 'white',
              borderRadius: 22,
              overflow: 'hidden',
              boxShadow: '0 30px 90px rgba(0,0,0,0.25)'
            }}
          >
            {(() => {
              const typeInfo = getTypeInfo(
                viewingEvent.event_type
              );

              return (
                <>
                  {/* MODAL HEADER */}
                  <div
                    style={{
                      background:
                        `linear-gradient(135deg, ${typeInfo.color}, ${typeInfo.color}cc)`,
                      padding: isMobile
                        ? '22px 20px'
                        : '26px 28px',
                      color: 'white',
                      position: 'relative'
                    }}
                  >
                    <button
                      onClick={closeViewModal}
                      style={{
                        position: 'absolute',
                        right: 18,
                        top: 18,
                        width: 34,
                        height: 34,
                        borderRadius: '50%',
                        border: 'none',
                        background: 'rgba(255,255,255,0.18)',
                        color: 'white',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <X size={16} />
                    </button>

                    <div
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: '6px 11px',
                        borderRadius: 20,
                        background: 'rgba(255,255,255,0.18)',
                        fontSize: 11,
                        fontWeight: 700,
                        marginBottom: 14
                      }}
                    >
                      {typeInfo.label}
                    </div>

                    <div
                      style={{
                        fontSize: isMobile ? 20 : 24,
                        fontWeight: 800,
                        lineHeight: 1.25,
                        paddingRight: 35,
                        wordBreak: 'break-word'
                      }}
                    >
                      {viewingEvent.title}
                    </div>
                  </div>

                  {/* MODAL İÇERİK */}
                  <div
                    style={{
                      padding: isMobile
                        ? '20px'
                        : '24px 28px'
                    }}
                  >

                    {/* TARİH */}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: 13,
                        background: '#f8fafc',
                        borderRadius: 13,
                        marginBottom: 20
                      }}
                    >
                      <div
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: 11,
                          background: typeInfo.bg,
                          color: typeInfo.color,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}
                      >
                        <Calendar size={18} />
                      </div>

                      <div>
                        <div
                          style={{
                            fontSize: 10,
                            color: '#94a3b8',
                            fontWeight: 700,
                            marginBottom: 3
                          }}
                        >
                          TARİH
                        </div>

                        <div
                          style={{
                            fontSize: 14,
                            color: '#1e293b',
                            fontWeight: 700,
                            textTransform: 'capitalize'
                          }}
                        >
                          {formatEventDate(viewingEvent)}
                        </div>
                      </div>
                    </div>

                    {/* SAAT */}
                <div style={{ marginBottom: 18 }}>
                  <label
                    style={{
                      display: 'block',
                      marginBottom: 7,
                      fontWeight: 700,
                      fontSize: 11,
                      color: '#64748b'
                    }}
                  >
                    ETKİNLİK SAATİ
                  </label>

                  <input
                    type="time"
                    value={form.event_time || ''}
                    onChange={e =>
                      setForm({
                        ...form,
                        event_time: e.target.value
                      })
                    }
                    style={{
                      width: '100%',
                      padding: '11px 13px',
                      border: '1.5px solid #e2e8f0',
                      borderRadius: 11,
                      fontSize: 14,
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                {/* AÇIKLAMA */}
                    <div
                      style={{
                        marginBottom: 24
                      }}
                    >
                      <div
                        style={{
                          fontSize: 10,
                          color: '#94a3b8',
                          fontWeight: 700,
                          marginBottom: 8
                        }}
                      >
                        AÇIKLAMA
                      </div>

                      <div
                        style={{
                          minHeight: 45,
                          fontSize: 14,
                          lineHeight: 1.65,
                          color: viewingEvent.description
                            ? '#475569'
                            : '#94a3b8',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word'
                        }}
                      >
                        {viewingEvent.description ||
                          'Bu etkinlik için açıklama eklenmemiş.'}
                      </div>
                    </div>

                    {/* AKSİYONLAR */}
                    <div
                      style={{
                        display: 'flex',
                        gap: 10,
                        flexDirection: isMobile
                          ? 'column'
                          : 'row'
                      }}
                    >
                      {isAdmin && (
                        <button
                          onClick={() =>
                            openEditModal(viewingEvent)
                          }
                          style={{
                            flex: 1,
                            padding: '12px 15px',
                            border: 'none',
                            borderRadius: 11,
                            background:
                              'linear-gradient(135deg, #007aff, #5856d6)',
                            color: 'white',
                            cursor: 'pointer',
                            fontWeight: 700,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 7
                          }}
                        >
                          <Pencil size={15} />
                          Düzenle
                        </button>
                      )}

                      {isAdmin && (
                        <button
                          onClick={e => {
                            const id = viewingEvent.id;
                            deleteEvent(id, e);
                          }}
                          style={{
                            padding: '12px 16px',
                            border: 'none',
                            borderRadius: 11,
                            background:
                              'rgba(239,68,68,0.08)',
                            color: '#ef4444',
                            cursor: 'pointer',
                            fontWeight: 700,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 7
                          }}
                        >
                          <Trash2 size={15} />
                          Sil
                        </button>
                      )}

                      <button
                        onClick={closeViewModal}
                        style={{
                          padding: '12px 18px',
                          border: '1px solid #e5e7eb',
                          borderRadius: 11,
                          background: 'white',
                          color: '#64748b',
                          cursor: 'pointer',
                          fontWeight: 600
                        }}
                      >
                        Kapat
                      </button>
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* =====================================================
          EKLE / DÜZENLE MODALI
         ===================================================== */}
      {showFormModal && (
        <div
          onClick={closeFormModal}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15,23,42,0.48)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1200,
            padding: 20
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              width: '100%',
              maxWidth: 500,
              background: 'white',
              borderRadius: 22,
              overflow: 'hidden',
              boxShadow: '0 30px 90px rgba(0,0,0,0.25)'
            }}
          >

            {/* HEADER */}
            <div
              style={{
                background:
                  'linear-gradient(135deg, #1a1a2e, #007aff)',
                padding: '20px 24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                color: 'white'
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: 11,
                    opacity: 0.75,
                    marginBottom: 4
                  }}
                >
                  {editingEvent
                    ? 'ETKİNLİK DÜZENLEME'
                    : 'YENİ ETKİNLİK'}
                </div>

                <div
                  style={{
                    fontSize: 17,
                    fontWeight: 800
                  }}
                >
                  {editingEvent
                    ? 'Etkinliği Düzenle'
                    : `${selectedDay} ${MONTHS_TR[currentDate.month]} ${currentDate.year}`}
                </div>
              </div>

              <button
                onClick={closeFormModal}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  border: 'none',
                  background: 'rgba(255,255,255,0.15)',
                  color: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <X size={15} />
              </button>
            </div>

            {/* FORM */}
            <div
              style={{
                padding: isMobile ? 20 : 24
              }}
            >

              {/* BAŞLIK */}
              <div style={{ marginBottom: 18 }}>
                <label
                  style={{
                    display: 'block',
                    marginBottom: 7,
                    fontWeight: 700,
                    fontSize: 11,
                    color: '#64748b'
                  }}
                >
                  BAŞLIK *
                </label>

                <input
                  autoFocus
                  value={form.title}
                  onChange={e =>
                    setForm({
                      ...form,
                      title: e.target.value
                    })
                  }
                  placeholder="Etkinlik başlığı..."
                  style={{
                    width: '100%',
                    padding: '11px 13px',
                    border: '1.5px solid #e2e8f0',
                    borderRadius: 11,
                    fontSize: 14,
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* TÜR */}
              <div style={{ marginBottom: 18 }}>
                <label
                  style={{
                    display: 'block',
                    marginBottom: 8,
                    fontWeight: 700,
                    fontSize: 11,
                    color: '#64748b'
                  }}
                >
                  ETKİNLİK TÜRÜ
                </label>

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns:
                      'repeat(3, 1fr)',
                    gap: 8
                  }}
                >
                  {EVENT_TYPES.map(type => {
                    const selected =
                      form.event_type === type.key;

                    return (
                      <button
                        key={type.key}
                        type="button"
                        onClick={() =>
                          setForm({
                            ...form,
                            event_type: type.key,
                            color: type.color
                          })
                        }
                        style={{
                          padding: '9px 5px',
                          border:
                            `1.5px solid ${selected ? type.color : '#e2e8f0'}`,
                          borderRadius: 10,
                          background: selected
                            ? type.bg
                            : 'white',
                          color: selected
                            ? type.color
                            : '#64748b',
                          cursor: 'pointer',
                          fontWeight: 700,
                          fontSize: 11
                        }}
                      >
                        {type.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* AÇIKLAMA */}
              <div style={{ marginBottom: 22 }}>
                <label
                  style={{
                    display: 'block',
                    marginBottom: 7,
                    fontWeight: 700,
                    fontSize: 11,
                    color: '#64748b'
                  }}
                >
                  AÇIKLAMA
                </label>

                <textarea
                  value={form.description}
                  onChange={e =>
                    setForm({
                      ...form,
                      description: e.target.value
                    })
                  }
                  rows={5}
                  placeholder="Etkinlik hakkında açıklama..."
                  style={{
                    width: '100%',
                    padding: '11px 13px',
                    border: '1.5px solid #e2e8f0',
                    borderRadius: 11,
                    fontSize: 14,
                    outline: 'none',
                    resize: 'vertical',
                    boxSizing: 'border-box',
                    fontFamily:
                      'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
                  }}
                />
              </div>

              {/* BUTONLAR */}
              <div
                style={{
                  display: 'flex',
                  gap: 9,
                  flexDirection: isMobile
                    ? 'column-reverse'
                    : 'row'
                }}
              >
                {editingEvent && isAdmin && (
                  <button
                    type="button"
                    onClick={e => {
                      const id = editingEvent.id;
                      deleteEvent(id, e);
                    }}
                    style={{
                      padding: '11px 16px',
                      border: 'none',
                      borderRadius: 11,
                      background:
                        'rgba(239,68,68,0.08)',
                      color: '#ef4444',
                      cursor: 'pointer',
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 6
                    }}
                  >
                    <Trash2 size={14} />
                    Sil
                  </button>
                )}

                <button
                  type="button"
                  onClick={closeFormModal}
                  style={{
                    flex: 1,
                    padding: '11px 15px',
                    border: '1px solid #e2e8f0',
                    borderRadius: 11,
                    background: 'white',
                    color: '#64748b',
                    cursor: 'pointer',
                    fontWeight: 600
                  }}
                >
                  Vazgeç
                </button>

                <button
                  type="button"
                  onClick={save}
                  disabled={
                    saving || !form.title.trim()
                  }
                  style={{
                    flex: 1.5,
                    padding: '11px 15px',
                    border: 'none',
                    borderRadius: 11,
                    background:
                      saving || !form.title.trim()
                        ? '#cbd5e1'
                        : 'linear-gradient(135deg, #007aff, #5856d6)',
                    color: 'white',
                    cursor:
                      saving || !form.title.trim()
                        ? 'not-allowed'
                        : 'pointer',
                    fontWeight: 700
                  }}
                >
                  {saving
                    ? 'Kaydediliyor...'
                    : editingEvent
                      ? 'Değişiklikleri Kaydet'
                      : 'Etkinliği Oluştur'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CONFIRM */}
      <ConfirmModal
        isOpen={confirmModal.open}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={() => {
          if (confirmModal.action) {
            confirmModal.action();
          }
        }}
        onCancel={closeConfirm}
        confirmText="Evet, Sil"
      />

    </AdminLayout>
  );
}
